<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";

import LeResultsListItem from "@/components/dictionary-selector/LeResultsListItem.vue";
import { MatomoHelper } from "@/helpers";
import { useSearchStore } from "@/stores";
import { type Dictionary, MatomoEventAction, MatomoEventCategory } from "@/types";

interface Props {
  companyDictionaries?: Dictionary[];
  companyDictionaryGroupHeading: string;
  favoriteDictionaries?: Dictionary[];
  resultFavoriteDictionaries?: Dictionary[];
  resultDictionaries?: Dictionary[];
  resultUpsellDictionaries?: Dictionary[];
  resultUpsellUnlockedDictionaries?: Dictionary[];
  selectedId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  companyDictionaries: () => [],
  favoriteDictionaries: () => [],
  resultFavoriteDictionaries: () => [],
  resultDictionaries: () => [],
  resultUpsellDictionaries: () => [],
  resultUpsellUnlockedDictionaries: () => [],
  selectedId: undefined
});

const emit = defineEmits<{
  (event: "select", dictionaryId: string): void;
  (event: "favorite", dictionaryId: string): void;
  (event: "unfavorite", dictionaryId: string): void;
}>();

const searchStore = useSearchStore();

const toast = useToast();
const { t } = useI18n();

const noFavoriteResultsMessage = computed<string>(() => {
  if (searchStore.filter?.sourceLanguage) {
    return t("le-results-list.favorites-collection.no-results.with-language-selector");
  }

  return t("le-results-list.favorites-collection.no-results.without-language-selector");
});

const noTopResultsMessage = computed<string>(() => {
  if (searchStore.filter?.sourceLanguage) {
    return t("le-results-list.results-collection.no-results.with-language-selector");
  }

  return t("le-results-list.results-collection.no-results.without-language-selector");
});

const isSelected = (dictionaryId: string) => dictionaryId === props.selectedId;
const isFavorite = (dictionaryId: string) =>
  props.favoriteDictionaries.map((dictionary) => dictionary.id).includes(dictionaryId);

const emitFavorite = (dictionaryId: string) => {
  MatomoHelper.sendEvent(MatomoEventCategory.Results, MatomoEventAction.AddFavorite, "Added favorite");

  emit("favorite", dictionaryId);
};

const emitUnFavorite = (dictionaryId: string) => {
  MatomoHelper.sendEvent(MatomoEventCategory.Results, MatomoEventAction.RemoveFavorite, "Removed favorite");

  emit("unfavorite", dictionaryId);
};

const select = (dictionaryId: string) => {
  toast.clear();
  emit("select", dictionaryId);
};

const selectSuggestedDictionary = (dictionaryId: string, dictionaryNumber: number) => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.Results,
    MatomoEventAction.SelectDictionaryTopResults,
    `Dictionary number ${dictionaryNumber}`
  );

  toast.clear();
  emit("select", dictionaryId);
};

const selectSuggestedUpsellDictionary = (dictionaryId: string, dictionaryNumber: number) => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.DictionaryUpsell,
    MatomoEventAction.SelectUpsellDictionaryTopResults,
    `Clicked upsell dictionary number ${dictionaryNumber} in top results`
  );

  toast.clear();
  emit("select", dictionaryId);
};
</script>

<template>
  <div class="le-results-list">
    <div v-if="props.companyDictionaries.length > 0" class="le-results-list__collection">
      <h3 class="le-results-list__collection-header">
        {{ props.companyDictionaryGroupHeading }}
      </h3>
      <div class="le-results-list__collection-content">
        <LeResultsListItem
          v-for="dictionary in props.companyDictionaries"
          :key="dictionary.id"
          class="le-results-list__item"
          :dictionary="dictionary"
          :is-selected="isSelected(dictionary.id)"
          :is-favorite="isFavorite(dictionary.id)"
          @select="select(dictionary.id)"
        />
      </div>
    </div>

    <div v-if="props.favoriteDictionaries.length > 0" class="le-results-list__collection">
      <h3 class="le-results-list__collection-header">
        {{ t("le-results-list.favorites-collection.header") }}
      </h3>
      <p
        v-if="props.resultFavoriteDictionaries.length === 0 && (searchStore.isLoaded || searchStore.isFetchingArticle)"
        class="le-results-list__collection-message"
      >
        {{ noFavoriteResultsMessage }}
      </p>
      <div class="le-results-list__collection-content">
        <LeResultsListItem
          v-for="dictionary in props.resultFavoriteDictionaries"
          :key="dictionary.id"
          show-favorite-toggle
          class="le-results-list__item"
          :dictionary="dictionary"
          :is-selected="isSelected(dictionary.id)"
          :is-favorite="isFavorite(dictionary.id)"
          @unfavorite="emitUnFavorite(dictionary.id)"
          @select="select(dictionary.id)"
        />
      </div>
    </div>

    <div class="le-results-list__collection">
      <h3 class="le-results-list__collection-header">
        {{ t("le-results-list.results-collection.header", props.resultDictionaries.length) }}
      </h3>
      <p
        v-if="
          props.resultDictionaries.length === 0 &&
          props.resultUpsellDictionaries.length === 0 &&
          (searchStore.isLoaded || searchStore.isFetchingArticle)
        "
        class="le-results-list__collection-message"
      >
        {{ noTopResultsMessage }}
      </p>
      <div class="le-results-list__collection-content">
        <LeResultsListItem
          v-for="(dictionary, index) in props.resultDictionaries"
          :key="dictionary.id"
          class="le-results-list__item"
          show-favorite-toggle
          :dictionary="dictionary"
          :is-selected="isSelected(dictionary.id)"
          :is-favorite="isFavorite(dictionary.id)"
          @favorite="emitFavorite(dictionary.id)"
          @unfavorite="emitUnFavorite(dictionary.id)"
          @select="selectSuggestedDictionary(dictionary.id, index + 1)"
        />
      </div>
      <div
        v-if="props.resultUpsellDictionaries"
        class="le-results-list__collection-content"
        :class="{ 'le-results-list__collection-content--spacing': props.resultDictionaries.length }"
      >
        <LeResultsListItem
          v-for="(dictionary, index) in props.resultUpsellDictionaries"
          :key="dictionary.id"
          class="le-results-list__item"
          is-upsell
          :is-unlocked="props.resultUpsellUnlockedDictionaries.includes(dictionary)"
          :dictionary="dictionary"
          :is-selected="isSelected(dictionary.id)"
          @select="selectSuggestedUpsellDictionary(dictionary.id, index + 1)"
        />
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";

.le-results-list {
  position: relative;

  &__collection {
    &-header {
      margin-top: 0px;
      margin-bottom: 12px;
      color: @colors[@neutrals][500];
      font-family: @font-family-heading;
      font-size: 20px;
      font-weight: 500;
      line-height: 28px;
    }

    &-message {
      .body-text("s");
      color: @colors[@neutrals][500];
    }

    &-content {
      border-radius: 4px;
      background-color: @colors[@neutrals][100];
      box-shadow: @shadows[dropdown];

      &--spacing {
        margin-top: 12px;
      }
    }
  }

  &__collection + &__collection {
    margin-top: 32px;
  }

  &__item {
    position: relative;

    &::after {
      content: "";
      position: absolute;
      right: 0;
      left: 0;
      height: 2px;
      transform: translateY(-50%);
      background-color: @colors[@secondary][200];
      pointer-events: none;
    }
  }

  &__item:first-child {
    border-radius: 4px 4px 0 0;
  }

  &__item:last-child {
    border-radius: 0 0 4px 4px;

    &::after {
      height: 0;
    }
  }

  &__item:only-child {
    border-radius: 4px;
  }
}
</style>
